import { variables, Flex, Text, Skeleton } from '@bitrise/bitkit';
import { SeriesPointXY, useGetNearestPoint, Viz, VizSVG, VizSVGLine } from '@bitrise/bitviz';
import React, { FunctionComponent, PointerEvent, useState } from 'react';
import max from 'lodash.max';
import min from 'lodash.min';
import useOverviewLiveUsers from '../useOverviewLiveUsers';
import getFormattedValue from '../../../utils/metric/getFormattedValue';
import OverviewLiveUsersTooltip from './OverviewLiveUsersTooltip';

const OverviewLiveUsers: FunctionComponent = () => {
  const { data, labels, isFetching } = useOverviewLiveUsers();
  const [focusPoint, setFocusPoint] = useState<null | SeriesPointXY>(null);
  const series = data ? data.map((y, x) => ({ x, y })) : [];
  const yMin = min(data) ?? 0;
  const yMax = max(data) ?? 1;

  const getNearestPoint = useGetNearestPoint(series, { axis: 'x' });

  const onPointerMove = (_: PointerEvent, point?: SeriesPointXY) => {
    if (point && point.x !== null) {
      const nearestPoint = getNearestPoint(point);

      if (nearestPoint && nearestPoint.x !== null) {
        setFocusPoint(nearestPoint);
        return;
      }
    }

    setFocusPoint(null);
  };

  return (
    <Skeleton active={isFetching}>
      <Flex
        alignChildrenVertical="middle"
        backgroundColor="gray-1"
        direction="horizontal"
        gap="x5"
        paddingHorizontal="x6"
        paddingVertical="x4"
        style={{ borderRadius: '100px' }}
      >
        <Flex>
          <Text>Live users</Text>
        </Flex>

        <Flex grow initial="none">
          <Viz
            height={32}
            onPointerMove={onPointerMove}
            onPointerLeave={onPointerMove}
            xDomain={[1, series.length - 1]}
            yDomain={[yMin, yMax]}
          >
            <VizSVG offsetArtboardClipMaskTop={2} offsetArtboardClipMaskBottom={2}>
              <VizSVGLine
                animate="slide"
                clipToArtboard
                curve="curveMonotoneX"
                opacity={1}
                originX={0}
                originY={yMin}
                series={series}
                stroke={variables.colorDarkblue2}
                strokeWidth="2"
              />
            </VizSVG>

            {!!focusPoint && focusPoint.x !== null && (
              <OverviewLiveUsersTooltip point={focusPoint} title={labels[focusPoint.x]} />
            )}
          </Viz>
        </Flex>

        <Flex>
          <Text config="4">{getFormattedValue('1', data && data[data?.length - 1])}</Text>
        </Flex>
      </Flex>
    </Skeleton>
  );
};

export default OverviewLiveUsers;
